import * as semver from 'semver';
import { PLATFORM_REGISTRY, toPlatformId, } from '../../contracts/platform-registry';
import { RISK_TEXT_COPY, } from '../../data/alert-risk-text-copy';
const PLACEHOLDER = /\{([A-Za-z]+)\}/g;
/**
 * Fills every `{name}` from facts. Returns null if a placeholder is missing or
 * blank, unless its name is in `optional`, which may render as empty.
 */
export function fillRiskTemplate(template, facts, optional = new Set()) {
    let missing = false;
    const filled = template.replace(PLACEHOLDER, (_match, name) => {
        const value = facts[name];
        if (value == null || (value.trim().length === 0 && !optional.has(name))) {
            missing = true;
            return '';
        }
        return value;
    });
    return missing ? null : filled;
}
/** Thrown by `riskCopy` for a key absent from the generated copy table. */
export class RiskCopyMissingError extends Error {
    constructor(key) {
        super(`KIR-4270: no risk-text copy for ${key}`);
        this.name = 'RiskCopyMissingError';
    }
}
/** 32-bit FNV-1a, for exact matches on text that cannot be committed (C13). */
export function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}
/** A sheet row's copy; the key is `subPolicyKey::type` plus `#variant`. */
export function riskCopy(key) {
    const copy = RISK_TEXT_COPY[key];
    if (copy == null) {
        throw new RiskCopyMissingError(key);
    }
    return copy;
}
/** Both parts, each falling back to the row's static text when null. */
export function partsOf(copy, whatHappened, whyItMatters) {
    return {
        whatHappened: whatHappened ?? copy.fallbackWhatHappened,
        whyItMatters: whyItMatters ?? copy.fallbackWhyItMatters,
    };
}
/** The row's static fallback for both parts. */
export function fallbackOf(copy) {
    return partsOf(copy, null, null);
}
/** Registry display name for a client id; undefined for null or unknown ids. */
export function clientDisplayName(clientType) {
    const id = toPlatformId(clientType);
    return id == null ? undefined : PLATFORM_REGISTRY[id].displayName;
}
/** A trimmed string, or undefined when empty. */
export function present(value) {
    const trimmed = value?.trim();
    return trimmed == null || trimmed.length === 0 ? undefined : trimmed;
}
/** A source-details field's normalized value, else what Kirin observed. */
export function sourceField(details, key) {
    if (details == null || !('fields' in details)) {
        return undefined;
    }
    const field = details.fields.find(f => f.key === key);
    if (field == null || field.isUnknown) {
        return undefined;
    }
    return present(field.allowBlockValue) ?? present(field.detected);
}
/** The backend's "version unknown" sentinel (`utils/version-utils.ts`). */
export const UNKNOWN_VERSION = '__UNKNOWN_VERSION__';
const PLAIN_RELEASE = /^\d+(?:\.\d+)*$/;
function comparePlainReleases(a, b) {
    const left = a.split('.').map(Number);
    const right = b.split('.').map(Number);
    for (let i = 0; i < Math.max(left.length, right.length); i++) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0)
            return diff;
    }
    return 0;
}
/**
 * Whether `fixed` is strictly newer than `checked` under the ecosystem's order:
 * semver for npm, PEP 440 release numbers for PyPI. Anything not comparable
 * (other ecosystems, pre-releases on PyPI, unparsable) is false.
 */
export function isNewerVersion(fixed, checked, ecosystem) {
    const eco = ecosystem?.toLowerCase();
    if (eco === 'npm') {
        return (semver.valid(fixed) != null &&
            semver.valid(checked) != null &&
            semver.gt(fixed, checked));
    }
    if (eco === 'pypi') {
        return (PLAIN_RELEASE.test(fixed) &&
            PLAIN_RELEASE.test(checked) &&
            comparePlainReleases(fixed, checked) > 0);
    }
    return false;
}
/**
 * The advisory summary clause: ": " + summary with trailing periods removed.
 * A summary already ending in "…", "!" or "?" keeps it; `closeSentence` then
 * drops the period the template adds after it.
 */
export function summaryClause(summary) {
    const text = present(summary);
    if (text == null)
        return '';
    let end = text.length;
    while (end > 0 && text[end - 1] === '.')
        end -= 1;
    return `: ${text.slice(0, end)}`;
}
/**
 * Drops the period the template adds right after a summary clause that
 * already ends a sentence ("…", "!" or "?"). Only that one period: other
 * text such as `obj?.constructor` is left alone.
 */
export function closeSentence(text, clause) {
    if (!/[…!?]$/.test(clause))
        return text;
    // The summary is the template's last clause before its period.
    const at = text.lastIndexOf(`${clause}.`);
    return at < 0
        ? text
        : text.slice(0, at + clause.length) + text.slice(at + clause.length + 1);
}
/**
 * Longest location or path the engine pattern-matches; longer input renders
 * the static fallback, which keeps the backtracking regexes bounded.
 */
export const MAX_MATCHED_LENGTH = 4096;
//# sourceMappingURL=common.js.map